import { TRACK_IDS } from "../../analytics/trackIds";
import React, { useEffect, useState, useContext, useRef } from "react";

import { createPortal } from "react-dom";
import PopupCloseIcon from "../../assets/icons/svg/PopupCloseIcon";
import ChannelControl from "../videoAlert/ChannelControl";
import { SessionContext } from "../../context/SessionContext";
import { checkProfilePin } from "../../api/user/user";
import localizationStrings from "../../statics/localization"; // prop drilling gerekebilir
import useDialog from "../../utils/useDialog";
import { flowCopy } from "../../utils/flowCopy";
import atlasLogo from "../../assets/logo/atlas-logo.png";
import ProfilePinForm from "../profileList/ProfilePinForm";

// Tracks a popup button by the meaning of its label; unknown labels get the
// fallback id.
const getPopupTrackId = (text, fallback) => {
  const labelIds = [
    [localizationStrings.yes, TRACK_IDS.POPUP_YES],
    [localizationStrings.no, TRACK_IDS.POPUP_NO],
    [localizationStrings.okay, TRACK_IDS.POPUP_OK],
    [localizationStrings.login?.confirm, TRACK_IDS.POPUP_OK],
    [localizationStrings.close, TRACK_IDS.POPUP_CLOSE],
    [localizationStrings.cancel, TRACK_IDS.POPUP_CANCEL]
  ];
  const match = labelIds.find(([label]) => !!label && label === text);
  return match ? match[1] : fallback;
};

const Notification = ({
  text,
  confirmText,
  notConfirmText,
  onClickConfirm,
  onClickNotConfirm,
  dontUsePortal,
  data,
  isPassword,
  persistPin = true,
  icon,
  sendResult,
  showCloseButton,
  viewComponent,
  theme,
  logo,
  switchButtons,
  profileCode,
  confirmTrackId,
  notConfirmTrackId,
  isCurrent = () => true
}) => {
  const [viewClass, setViewClass] = useState("");
  const [profilePassword, setProfilePassword] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const { profile, setProfile, allProfiles } = useContext(SessionContext);
  const pinProfile = profileCode && String(profileCode) !== String(profile?.id)
    ? allProfiles?.find((item) => String(item.id || item.profilecode) === String(profileCode))
    : profile;

  const attempt = useRef(0);
  const closed = useRef(false);
  const pendingRef = useRef(false);
  const [pending, setPending] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const callbackTimer = useRef(null);

  useEffect(() => {
    const opening = setTimeout(() => setViewClass("open"), 20);
    return () => {
      closed.current = true;
      attempt.current++;
      clearTimeout(opening);
      clearTimeout(callbackTimer.current);
    };
  }, []);

  const checkPassword = async (password) => {
    if (pendingRef.current || closed.current || !isCurrent()) return;
    if (!/^\d{4}$/.test(password)) { setShowAlert(true); return; }
    pendingRef.current = true;
    setPending(true);
    setRequestError(false);
    const version = ++attempt.current;
    try {
      const stored = JSON.parse(localStorage.getItem("profile") || "null");
      const id = profileCode || profile?.id || stored?.id;
      if (!id) throw new Error("Missing PIN profile");
      const { data } = await checkProfilePin(id, password);
      if (closed.current || version !== attempt.current || !isCurrent()) return;
      if (data === true) {
        if (persistPin && profile && String(profile.id) === String(id)) {
          const updatedProfile = { ...profile, pin: password };
          setProfile(updatedProfile);
          localStorage.setItem("profile", JSON.stringify({ ...updatedProfile, pin: window.btoa(password) }));
        }
        confirmClicked(password);
      } else {
        setShowAlert(true);
      }
    } catch (error) {
      if (!closed.current && version === attempt.current) setRequestError(true);
    } finally {
      if (!closed.current && version === attempt.current) {
        pendingRef.current = false;
        setPending(false);
      }
    }
  };

  const confirmClicked = (validatedPin = null) => {
    if (closed.current) return;
    closed.current = true;
    setViewClass("");
    if (typeof sendResult === "function") {
      if (isPassword) sendResult(true, validatedPin);
      else sendResult(true);
    }
    if (typeof onClickConfirm === "function") {
      callbackTimer.current = setTimeout(() => onClickConfirm(), 300);
    }
  };

  const notConfirmClicked = () => {
    if (closed.current) return;
    closed.current = true;
    attempt.current++;
    setViewClass("");
    clearTimeout(callbackTimer.current);
    if (typeof onClickNotConfirm === "function") {
      callbackTimer.current = setTimeout(() => onClickNotConfirm(), 300);
    }
  };

  const dialogRef = useRef(null);
  useDialog(dialogRef, onClickNotConfirm ? notConfirmClicked : undefined);

  const confirmButtonTrackId = confirmTrackId || getPopupTrackId(confirmText, TRACK_IDS.POPUP_CONFIRM);
  const notConfirmButtonTrackId = notConfirmTrackId || getPopupTrackId(notConfirmText, TRACK_IDS.POPUP_CANCEL);
  const renderedEl = isPassword ? (
    <div className={`notification-container profile-pin-notification ${viewClass}`}>
      <div
        className="notification-box profile-pin-box"
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-profile-title"
      >
        <ProfilePinForm
          idPrefix="notification-profile"
          profile={{ ...pinProfile, hasPin: true }}
          avatar={pinProfile?.avatar || pinProfile?.posterimage}
          value={profilePassword}
          onChange={(value) => {
            setProfilePassword(value);
            setShowAlert(false);
            setRequestError(false);
          }}
          onSubmit={() => checkPassword(profilePassword)}
          onCancel={notConfirmClicked}
          loading={pending}
          canCancel
          note={text}
          submitTrackId={confirmButtonTrackId}
          cancelTrackId={notConfirmButtonTrackId}
          error={requestError ? flowCopy().error : showAlert ? localizationStrings.wrongPinText : ""}
        />
      </div>
    </div>
  ) : (
    <div className={`notification-container ${viewClass}`}>
      <div
        className={`notification-box ${theme ? `${theme}-theme` : ""}`}
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={typeof text === "string" ? text : undefined}
        style={{
          width: viewComponent ? "auto" : data?.length ? "40%" : ""
        }}
      >
        {showCloseButton ? (
          <div className="close-row">
            <button
              type="button"
              aria-label={localizationStrings.close || "Close"}
              data-track-id={TRACK_IDS.POPUP_CLOSE}
              className="close"
              onClick={() => notConfirmClicked()}
            >
              <PopupCloseIcon />
            </button>
          </div>
        ) : null}
        {logo ? (
          <div
            className="logo"
            style={{ textAlign: "center", marginBottom: "10px" }}
          >
            <img src={atlasLogo} alt="Atlas Logo" />
          </div>
        ) : null}
        {icon ? <div className="icon">{icon}</div> : null}
        <p className="text">{text}</p>

        {viewComponent ? (
          <div>
            <ChannelControl />
          </div>
        ) : null}

        {requestError && <p role="alert">{flowCopy().error}</p>}
        <div className="button-wrapper">
          {switchButtons ? (
            <>
              <button
                type="button"
                disabled={pending}
                data-track-id={confirmButtonTrackId}
                className={`button-notification confirm ${
                  !confirmText?.length ? "hide" : ""
                }`}
                // style={{
                //   marginLeft:
                //     confirmText?.length && notConfirmText?.length ? "1.35vw" : ""
                // }}
                onClick={() => {
                  confirmClicked();
                }}
              >
                <span>{confirmText}</span>
              </button>
              <button
                type="button"
                data-track-id={notConfirmButtonTrackId}
                className={`button-notification notconfirm ${
                  !notConfirmText?.length ? "hide" : ""
                }`}
                onClick={() => {
                  notConfirmClicked();
                }}
              >
                <span>{notConfirmText}</span>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                data-track-id={notConfirmButtonTrackId}
                className={`button-notification notconfirm ${
                  !notConfirmText?.length ? "hide" : ""
                }`}
                onClick={() => {
                  notConfirmClicked();
                }}
              >
                <span>{notConfirmText}</span>
              </button>
              <button
                type="button"
                disabled={pending}
                data-track-id={confirmButtonTrackId}
                className={`button-notification confirm ${
                  !confirmText?.length ? "hide" : ""
                }`}
                // style={{
                //   marginLeft:
                //     confirmText?.length && notConfirmText?.length ? "1.35vw" : ""
                // }}
                onClick={() => {
                  confirmClicked();
                }}
              >
                <span>{confirmText}</span>
              </button>
            </>
          )}
        </div>

        <div style={{ display: "flex", marginTop: data?.length ? "30px" : "" }}>
          {data
            ? data.map((item) => {
                return (
                  <div
                    style={{ width: `${100 / data.length}%`, padding: "10px" }}
                  >
                    <img
                      style={{ width: "100%", height: "auto" }}
                      src={
                        item.posterpath + "" + item.posterfilelist.split(";")[5]
                      }
                    />
                  </div>
                );
              })
            : null}
        </div>
      </div>
    </div>
  );

  return dontUsePortal
    ? renderedEl
    : createPortal(renderedEl, document.getElementById("notification_root"));
};

export default Notification;
